import { useState } from "react";
import Card from "./Card";
import Button from "Components/Common/Button/Button";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { DrawInspectionData } from "../DrawHistory.types";
import { formatDate } from "utils/Common/Date";
import EmptyState from "Components/Common/EmptyState";
import EmptyInspectorIcon from "Assets/Img/EmptyInspectorIcon";

interface InspectionTabProps {
  data: DrawInspectionData | null;
  loading: boolean;
  onViewReport: () => void;
  viewingReport?: boolean;
}

const InspectionTab = ({
  data,
  loading,
  onViewReport,
  viewingReport = false,
}: InspectionTabProps) => {
  const [preview, setPreview] = useState<string | null>(null);

  if (loading) {
    return (
      <>
        <div className="sectionTitle">CSC Field Inspection</div>
        <Skeleton height={20} borderRadius={4} style={{ marginBottom: 10 }} />
        <div className="inspSummary inspLoading">
          {["blue", "amber", "green"].map((tone) => (
            <div className="inspMetric" key={tone}>
              <Skeleton
                width={40}
                height={40}
                borderRadius={4}
                style={{
                  display: "block",
                  marginBottom: 6,
                }}
              />
              <Skeleton
                width={80}
                height={10}
                borderRadius={4}
                style={{ display: "block" }}
              />
            </div>
          ))}
        </div>

        <Card title="Inspection Summary">
          <Skeleton
            height={13}
            width="30%"
            borderRadius={4}
            style={{ marginBottom: 8 }}
          />
          <Skeleton
            height={13}
            borderRadius={4}
            width="15%"
            style={{ marginBottom: 8 }}
          />
          <Skeleton height={13} borderRadius={4} width="25%" />
        </Card>

        <Card title="Inspection Photos">
          <div className="inspPhotoGrid">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} height={90} borderRadius={8} />
            ))}
          </div>
        </Card>
      </>
    );
  }

  const inspection = data?.inspection;

  if (!inspection) {
    return (
      <>
        <div className="sectionTitle">CSC Field Inspection</div>
        <EmptyState
          icon={<EmptyInspectorIcon />}
          title="No CSC inspection report available for this draw."
        />
      </>
    );
  }

  const flagCount = data?.flagCount ?? 0;
  const photos = data?.photos ?? [];

  return (
    <>
      <div className="sectionTitle">CSC Field Inspection</div>
      <div className="sectionSub">
        Inspection #{inspection.inspectionNumber}
        {inspection.inspectionDate
          ? ` · Conducted ${formatDate(inspection.inspectionDate, false)}`
          : ""}
        {inspection.inspectorName ? ` by ${inspection.inspectorName}` : ""}
      </div>

      <div className="inspSummary">
        <div className="inspMetric">
          <div className="inspMetricVal blue">
            {inspection.cscEstimation ?? "—"}%
          </div>
          <div className="inspMetricLabel">CSC Estimate</div>
        </div>
        <div className="inspMetric">
          <div className="inspMetricVal amber">{flagCount}</div>
          <div className="inspMetricLabel">Flags Raised</div>
        </div>
        <div className="inspMetric">
          <div className="inspMetricVal green">{photos.length}</div>
          <div className="inspMetricLabel">Photos Captured</div>
        </div>
      </div>

      <Card
        title="Inspection Summary"
        action={
          <Button
            type="button"
            className="btn secondary sm"
            onClick={onViewReport}
            disabled={viewingReport}
          >
            {viewingReport ? "Opening…" : "📄 View Full Report"}
          </Button>
        }
      >
        <div className="inspNarrative">
          {inspection.note?.general && <p>{inspection.note.general}</p>}
          {inspection.note?.flags && (
            <p className="flagNote">
              <strong>Flags:</strong> {inspection.note.flags}
            </p>
          )}
          {inspection.note?.offsite && (
            <p>
              <strong>Off-site materials:</strong> {inspection.note.offsite}
            </p>
          )}
          {!inspection.note?.general &&
            !inspection.note?.flags &&
            !inspection.note?.offsite && (
              <EmptyState
                noBorder
                icon={<EmptyInspectorIcon />}
                title="No inspection narrative recorded."
              />
            )}
        </div>
      </Card>

      <Card title={`Inspection Photos (${photos.length})`}>
        {photos.length === 0 ? (
          <EmptyState
            noBorder
            icon={<EmptyInspectorIcon />}
            title="No inspection photos captured."
          />
        ) : (
          <div className="inspPhotoGrid">
            {photos.map((photo) => (
              <button
                type="button"
                key={photo.id}
                className="inspPhotoThumb"
                onClick={() => photo.url && setPreview(photo.url)}
                disabled={!photo.url}
              >
                {photo.url ? (
                  <img
                    src={photo.url}
                    alt={photo.fileName ?? "Inspection photo"}
                  />
                ) : (
                  "🏗"
                )}
              </button>
            ))}
          </div>
        )}
      </Card>

      {preview && (
        <div
          className="inspPhotoLightbox"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreview(null)}
        >
          <div className="rel">
            <button
              type="button"
              className="inspPhotoLightboxClose"
              aria-label="Close"
              onClick={() => setPreview(null)}
            >
              ×
            </button>
            <img
              src={preview}
              alt="Inspection"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default InspectionTab;
